import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Activity, AlertTriangle, HeartPulse, ArrowUpRight } from 'lucide-react';
import Header from '@/components/Header';
import InitialSituation from '@/components/InitialSituation';
import ABCDESections from '@/components/ABCDESections';
import SAMPLERSection from '@/components/SAMPLERSection';
import STUSection from '@/components/STUSection';
import MeasuresSection from '@/components/MeasuresSection';
import AdditionalOptions from '@/components/AdditionalOptions';
import ResultPreview from '@/components/ResultPreview';
import { generateAnamnesisText } from '@/utils/textGenerator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { createEmptyForm, loadSavedForm, INACTIVITY_TIMEOUT, STORAGE_KEY, TIMESTAMP_KEY } from '@/utils/formState';

export default function AnamnesisGenerator() {
  const [formData, setFormData] = useState(() => loadSavedForm());

  const [generatedText, setGeneratedText] = useState('');
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [counterStats, setCounterStats] = useState({ total: 0, current_month: 0 });

  // Form data is restored before the first render; only fetch counters on mount.
  useEffect(() => {
    fetchCounterStats();
  }, []);

  const fetchCounterStats = async () => {
    // Get backend URL
    const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
    
    // Skip if no backend URL configured
    if (!backendUrl) {
      console.log('No backend URL configured, skipping counter stats');
      return;
    }
    
    try {
      const response = await fetch(`${backendUrl}/api/counter`);
      if (response.ok) {
        const data = await response.json();
        setCounterStats(data);
      }
    } catch (error) {
      console.log('Counter stats not available:', error.message);
      // Fail silently - counter is not critical
    }
  };

  const incrementCounter = async () => {
    // Get backend URL
    const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
    
    // Skip if no backend URL configured
    if (!backendUrl) {
      return;
    }
    
    try {
      const response = await fetch(`${backendUrl}/api/counter/increment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setCounterStats({
          total: data.total,
          current_month: data.current_month
        });
      }
    } catch (error) {
      console.log('Counter increment failed:', error.message);
      // Fail silently - counter is not critical
    }
  };

  // Save to localStorage and update timestamp on data change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    localStorage.setItem(TIMESTAMP_KEY, Date.now().toString());
    setLastActivity(Date.now());
  }, [formData]);

  // Auto-clear on inactivity
  useEffect(() => {
    const checkInactivity = setInterval(() => {
      const timeSinceActivity = Date.now() - lastActivity;
      if (timeSinceActivity >= INACTIVITY_TIMEOUT) {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(TIMESTAMP_KEY);
        setFormData(createEmptyForm());
        setGeneratedText('');
      }
    }, 60000); // Check every minute

    return () => clearInterval(checkInactivity);
  }, [lastActivity]);

  const handleGenerateText = useCallback(() => {
    const text = generateAnamnesisText(formData);
    setGeneratedText(text);
    // Increment counter when text is generated
    incrementCounter();
  }, [formData]);

  const handleReset = () => {
    setFormData(createEmptyForm());
    setGeneratedText('');
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(TIMESTAMP_KEY);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-8 max-w-6xl" role="main">
        {/* Hero Section with SEO-optimized content */}
        <header className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4" aria-hidden="true">
            <Activity className="w-8 h-8 text-primary" aria-label="Medizinisches Herzschlag-Icon" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-4">
            Anamnese Generator für Rettungsdienst
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Strukturierte Patientenbeurteilung nach <strong>ABCDE-Schema</strong> und <strong>SAMPLER-Anamnese</strong> für Notfallmedizin und Rettungsdienst. 
            Mit <strong>BEFAST-Test</strong> zur Schlaganfall-Erkennung, <strong>EKG-Befundung</strong> und <strong>Trauma-Untersuchung</strong>.
          </p>
          <Link to="/ekg-hilfe" data-testid="open-ekg-helper-button" className="inline-flex items-center gap-2 mt-5 px-5 py-3 rounded-md border border-primary/30 text-teal-800 bg-white hover:bg-primary/5 transition-colors text-sm font-semibold"><HeartPulse size={18} />EKG-Hilfe öffnen<ArrowUpRight size={16} /></Link>
        </header>

        {/* SEO-relevant content section */}
        <section className="mb-8 text-center" aria-label="Features">
          <div className="max-w-4xl mx-auto">
            <h2 className="sr-only">Funktionen des Anamnese Generators</h2>
            <p className="text-sm text-muted-foreground">
              Perfekt für <strong>Notfallsanitäter</strong>, <strong>Rettungssanitäter</strong>, <strong>Notärzte</strong> und <strong>Rettungsdienstpersonal</strong>. 
              Automatische Generierung medizinischer Dokumentation nach etablierten Standards der präklinischen Notfallversorgung.
            </p>
          </div>
        </section>

        {/* Disclaimer */}
        <Alert className="mb-8 border-warning/30 bg-warning/5" role="alert">
          <AlertTriangle className="h-5 w-5 text-warning" aria-hidden="true" />
          <AlertDescription className="text-sm leading-relaxed ml-2">
            <strong>Hinweis:</strong> Die generierten Texte sind Vorschläge und dienen ausschließlich als Orientierungshilfe. 
            Sie stellen keine rechtlich verbindlichen Dokumentationen dar und ersetzen nicht die eigenverantwortliche, 
            sorgfältige Dokumentation durch medizinisches Fachpersonal.
          </AlertDescription>
        </Alert>

        {/* Form Sections with semantic HTML */}
        <div className="space-y-6">
          <section aria-label="Ausgangssituation">
            <InitialSituation 
              data={formData.initial}
              onChange={(data) => setFormData(prev => ({ ...prev, initial: data }))}
            />
          </section>

          <section aria-label="ABCDE-Schema">
            <ABCDESections 
              data={formData.abcde}
              onChange={(data) => setFormData(prev => ({ ...prev, abcde: data }))}
            />
          </section>

          <section aria-label="SAMPLER-Schema">
            <SAMPLERSection 
              data={formData.sampler}
              onChange={(data) => setFormData(prev => ({ ...prev, sampler: data}))}
            />
          </section>

          <section aria-label="Schnelle Trauma-Untersuchung">
            <STUSection
              data={formData.stu}
              onChange={(data) => setFormData(prev => ({ ...prev, stu: data }))}
            />
          </section>

          <section aria-label="Durchgeführte Maßnahmen">
            <MeasuresSection
              data={formData.measures}
              onChange={(data) => setFormData(prev => ({ ...prev, measures: data }))}
            />
          </section>

          <section aria-label="Zusätzliche Optionen">
            <AdditionalOptions 
              data={formData.additional}
              onChange={(data) => setFormData(prev => ({ ...prev, additional: data }))}
            />
          </section>
        </div>

        {/* Generate Button */}
        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={handleGenerateText}
            data-testid="generate-report-button"
            className="btn-medical px-8 py-3 rounded-lg font-semibold text-lg shadow-md"
            aria-label="Anamnese-Text generieren"
          >
            Text generieren
          </button>
          <button
            onClick={handleReset}
            data-testid="reset-form-button"
            className="px-8 py-3 rounded-lg font-semibold text-lg border-2 border-border bg-background hover:bg-muted transition-all"
            aria-label="Alle Eingaben zurücksetzen"
          >
            Zurücksetzen
          </button>
        </div>

        {/* Result Preview */}
        {generatedText && (
          <section aria-label="Generierter Anamnesetext">
            <ResultPreview text={generatedText} onReset={handleReset} />
          </section>
        )}
      </main>

      {/* Footer with SEO-relevant links */}
      <footer className="mt-16 py-8 border-t border-border" role="contentinfo">
        <div className="container mx-auto px-4 text-center">
          {/* Counter Stats - always show */}
          <div className="mb-6 text-muted-foreground">
            <p className="text-sm">
              Bisher wurden <strong className="text-primary">{counterStats.total.toLocaleString('de-DE')}</strong> Anamnesen mit diesem Tool erstellt
            </p>
            <p className="text-xs mt-1">
              Davon <strong>{counterStats.current_month.toLocaleString('de-DE')}</strong> im aktuellen Monat
            </p>
          </div>
          
          <nav aria-label="Footer Navigation">
            <div className="flex flex-wrap justify-center gap-4 text-sm text-muted-foreground mb-4">
              <Link to="/impressum" className="hover:text-primary transition-colors">
                Impressum
              </Link>
              <span aria-hidden="true">•</span>
              <Link to="/datenschutz" className="hover:text-primary transition-colors">
                Datenschutz
              </Link>
            </div>
          </nav>
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Creativey Media • Alle Daten werden lokal gespeichert • Automatische Löschung nach 15 Minuten Inaktivität
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            Anamnese Generator für Rettungsdienst | ABCDE-Schema | SAMPLER-Anamnese | BEFAST-Test | EKG-Befundung
          </p>
          <p data-testid="app-version" className="text-xs text-muted-foreground/70 mt-2">
            Version 1.2.0
          </p>
        </div>
      </footer>
    </div>
  );
}
